import { computed, nextTick, ref, watch, type ComputedRef } from 'vue'

import {
  useRoute,
  useRouter,
  type LocationQuery,
  type LocationQueryRaw,
} from 'vue-router'

import { fetchChatHtmlPages } from '@/apiClient/queries/useChatQueries'
import type { IHtmlPageDescriptor } from '@/apiClient/types/chat'
import { useChatStore } from '@/store/chat'
import { usePreviewStore } from '@/store/preview'
import { useToast } from '@/store/toast'
import { htmlBlocksInMessage } from '@/utils/chatHtmlBlocks'
import { websiteMetaForHtmlBlock } from '@/utils/publishing'

/**
 * Query params that ask the Chat page to open one of the chat's generated pages in
 * the preview panel on arrival — what "Edit content" (EVGPT-1546) navigates with, so
 * the user lands on the chat *and* the page they were editing rather than having to
 * find it. `message` is the website's `source_message_id`: only a fallback for when
 * the descriptor list can't be read, since it doesn't pin down the block within it.
 */
const WEBSITE_QUERY_PARAM = 'website'
const MESSAGE_QUERY_PARAM = 'message'

export interface WebsitePreviewIntent {
  websiteId: string
  /** Message the website was last published from, when the caller knows it. */
  messageId?: string
}

/** Query for `router.push({ name: 'Chat', ... })` that carries the intent. */
export function websitePreviewIntentQuery(
  intent: WebsitePreviewIntent,
): LocationQueryRaw {
  return {
    [WEBSITE_QUERY_PARAM]: intent.websiteId,
    ...(intent.messageId ? { [MESSAGE_QUERY_PARAM]: intent.messageId } : {}),
  }
}

function readWebsitePreviewIntent(
  query: LocationQuery,
): WebsitePreviewIntent | null {
  const websiteId = query[WEBSITE_QUERY_PARAM]
  if (typeof websiteId !== 'string' || !websiteId) return null
  const messageId = query[MESSAGE_QUERY_PARAM]
  return {
    websiteId,
    messageId:
      typeof messageId === 'string' && messageId ? messageId : undefined,
  }
}

/** The page the intent points at, resolved against the chat's descriptor list. */
export interface WebsitePreviewTarget {
  messageId: string
  /** Unknown when resolved from the intent's `message` fallback alone. */
  blockIndex?: number
  isPublished?: boolean
}

/**
 * Which generated page the website is served from. The descriptor list (EVGPT-1565)
 * is the complete, document-ordered set for the chat — independent of message
 * pagination — so it answers this without loading a single message. A website links
 * to one page at a time; if several ever carry the link, the last in document order
 * is the live one. Falls back to the intent's own message ref when the list is
 * unavailable (request failed) or predates the link.
 */
export function resolveWebsitePreviewTarget(
  descriptors: IHtmlPageDescriptor[] | undefined,
  intent: WebsitePreviewIntent,
): WebsitePreviewTarget | null {
  const pages = descriptors ?? []
  const linked = pages.filter((page) => page.website_id === intent.websiteId)
  const byMessage = intent.messageId
    ? pages.filter((page) => page.message_id === intent.messageId)
    : []
  const page = linked.at(-1) ?? byMessage.at(-1)
  if (page) {
    return {
      messageId: page.message_id,
      blockIndex: page.block_index,
      isPublished: page.is_published,
    }
  }
  return intent.messageId ? { messageId: intent.messageId } : null
}

/**
 * Opens the chat's published page in the preview panel when the route carries a
 * website preview intent (see {@link websitePreviewIntentQuery}).
 *
 * Costs one descriptor request plus — at most — one message: the target page usually
 * sits outside the 20-message window the chat opens with, and `ensureMessageLoaded`
 * fetches exactly that message (`GET /chats/{id}/messages/{id}`) instead of paging
 * back through the history to reach it.
 *
 * `isChatReady` gates the open on the chat's own load having settled: the chat view
 * auto-previews the newest generated page when messages arrive, which would otherwise
 * land after — and replace — the page we opened.
 */
export function useChatWebsitePreviewIntent(
  chatId: ComputedRef<string | undefined>,
  isChatReady: ComputedRef<boolean>,
) {
  const route = useRoute()
  const router = useRouter()
  const chatStore = useChatStore()
  const previewStore = usePreviewStore()

  const isOpeningPreview = ref(false)

  const pendingIntent = computed(() =>
    chatId.value && isChatReady.value
      ? readWebsitePreviewIntent(route.query)
      : null,
  )

  async function openIntentPreview(
    targetChatId: string,
    intent: WebsitePreviewIntent,
  ) {
    // Drop the params before opening, not after: the dashboard layout closes the
    // preview on every route change, so replacing the URL afterwards would close the
    // panel we just opened.
    const query = { ...route.query }
    delete query[WEBSITE_QUERY_PARAM]
    delete query[MESSAGE_QUERY_PARAM]
    await router.replace({ query })
    await nextTick()

    const descriptors = await fetchChatHtmlPages(targetChatId).catch(
      () => undefined,
    )
    const target = resolveWebsitePreviewTarget(descriptors, intent)
    if (!target) return false

    if (
      !(await chatStore.ensureMessageLoaded(targetChatId, target.messageId))
    ) {
      return false
    }
    const message = chatStore.getChatMessage(targetChatId, target.messageId)
    const blocks = htmlBlocksInMessage(message)
    if (!blocks.length) return false

    // With no block index (descriptor list unavailable) the message's own website
    // link picks the block out; a single-page message then falls back to its only one.
    const block =
      (target.blockIndex !== undefined
        ? blocks.find((candidate) => candidate.blockIndex === target.blockIndex)
        : blocks.find(
            (candidate) =>
              websiteMetaForHtmlBlock(message?.files, candidate.blockIndex)
                ?.websiteId === intent.websiteId,
          )) ?? blocks[0]

    // The user may have moved on while the page loaded; opening then would drop a
    // stale preview on top of whatever they navigated to.
    if (chatId.value !== targetChatId) return true

    const meta = websiteMetaForHtmlBlock(message?.files, block.blockIndex)
    previewStore.openPreview({
      type: 'html',
      raw: block.raw.trim(),
      htmlPublishKey: {
        chatId: targetChatId,
        messageId: target.messageId,
        blockIndex: block.blockIndex,
      },
      websiteId: meta?.websiteId ?? intent.websiteId,
      isWebsitePublished: meta?.isPublished ?? target.isPublished ?? false,
    })
    return true
  }

  watch(
    pendingIntent,
    async (intent) => {
      const targetChatId = chatId.value
      if (!intent || !targetChatId || isOpeningPreview.value) return
      isOpeningPreview.value = true
      try {
        if (!(await openIntentPreview(targetChatId, intent))) {
          useToast().onError('Could not open this webpage in the preview.')
        }
      } finally {
        isOpeningPreview.value = false
      }
    },
    { immediate: true },
  )

  return { isOpeningPreview }
}
